import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../constants/language';
import { schoolService } from '../../../services/school-service';
import { School } from '../../../models/school/school';
import { LoadingSpinner, UniversalEnrollButton } from '../../ui';
import { usePageSEO } from '../../../hooks';

const Schools: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the schools page
  usePageSEO('schools');

  const { t, i18n } = useTranslation();
  const [schoolsArray, setSchoolsArray] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSchools = async () => {
      setIsLoading(true);
      try {
        const result = await schoolService.getSchools(i18n.language as Language);
        setSchoolsArray(result.data);
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchools();
  }, [i18n.language]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                {t('pages.schools.heroTag', 'Transform Your Life')}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {t('pages.schools.heroTitle', 'ZOE Schools')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300">
                  {t('pages.schools.heroSubtitle', 'Empowering Kingdom Leaders')}
                </span>
              </h1>

              <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-lg mx-auto text-center">
                {t('pages.schools.heroDescription', 'Intensive training programs designed to equip believers with supernatural ministry skills and spiritual authority for effective kingdom service.')}
              </p>

              <div className="flex justify-center">
                <button
                  onClick={() => document.querySelector('#schools-grid')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-black hover:bg-gray-100 px-6 py-2.5 text-sm font-semibold transition-colors duration-300 rounded-lg flex items-center justify-center space-x-2"
                >
                  <span>{t('pages.schools.exploreSchools', 'Explore Schools')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schools Academy Layout */}
      <section id="schools-grid" className="bg-gradient-to-b from-white to-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {schoolsArray.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {t('pages.schools.noSchools', 'No Schools Available')}
              </h3>
              <p className="text-gray-600 text-lg">
                {t('pages.schools.noSchoolsMessage', 'Check back soon for upcoming school sessions and training programs.')}
              </p>
            </div>
          ) : (
            <>
              {/* Featured School Section */}
              {schoolsArray.length > 0 && (
                <div className="mb-20">
                  <div className="text-center mb-12">
                    <div className="inline-flex items-center px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                      </svg>
                      {t('pages.schools.featuredProgram', 'Featured Program')}
                    </div>
                    <h2 className="text-5xl md:text-6xl font-bold text-black mb-4">
                      {t('pages.schools.transformYourLife', 'Transform Your Life')}
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                      {t('pages.schools.featuredDescription', 'Discover our flagship school program designed to unlock your supernatural potential')}
                    </p>
                  </div>

                  {/* Featured School Card */}
                  <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
                    <div className="relative grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
                      {/* Content Side */}
                      <div className="p-8 lg:p-12 flex flex-col justify-center text-white">
                        <h3 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                          {schoolsArray[0].title}
                        </h3>

                        <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                          {schoolsArray[0].shortDescription}
                        </p>

                        {/* Stats Row */}
                        <div className="grid grid-cols-2 gap-6 mb-8">
                          <div>
                            <div className="text-2xl font-bold text-white">{schoolsArray[0].duration}</div>
                            <div className="text-sm text-gray-300">{t('pages.schools.duration', 'Duration')}</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-white">{schoolsArray[0].address.name}</div>
                            <div className="text-sm text-gray-300">{t('pages.schools.location', 'Location')}</div>
                          </div>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <Link
                            to={`/schools/${schoolsArray[0].key}/${schoolsArray[0].id}`}
                            className="bg-white text-black hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 text-center"
                          >
                            {t('pages.schools.learnMore', 'Learn More')}
                          </Link>
                          <UniversalEnrollButton
                            enrollmentUrl={schoolsArray[0].enrollmentForm}
                            variant="white-border"
                            size="lg"
                            className="px-8 py-4 rounded-xl font-semibold text-lg"
                          />
                        </div>
                      </div>

                      {/* Visual Side */}
                      <div className="relative hidden lg:flex items-center justify-center p-12">
                        <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 rounded-2xl flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                            <div className="text-white/80 text-lg">
                              {t('pages.schools.transformativeEducation', 'Transformative Education')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All Schools Academic Grid */}
              {schoolsArray.length > 0 && (
                <div>
                  <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                      {t('pages.schools.allPrograms', 'All Programs')}
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                      {t('pages.schools.schoolsDescription', 'Choose from our intensive training programs designed to equip you with supernatural ministry skills and spiritual authority.')}
                    </p>
                  </div>

                  {/* Academic-Style Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                    {schoolsArray.map((school, index) => (
                      <div
                        key={school.id}
                        className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
                      >
                        {/* School Header */}
                        <div className="relative h-32 bg-gradient-to-br from-gray-900 to-black">
                          <div className="absolute bottom-4 left-4 right-4">
                            <h3 className="text-xl font-bold text-white leading-tight line-clamp-2">
                              {school.title}
                            </h3>
                          </div>
                        </div>

                        {/* School Content */}
                        <div className="p-6">
                          <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                            {school.shortDescription}
                          </p>

                          {/* School Stats */}
                          <div className="space-y-3 mb-6">
                            <div className="flex items-center text-sm text-gray-500">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {school.duration}
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {school.address.name}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-3">
                            <Link
                              to={`/schools/${school.key}/${school.id}`}
                              className="w-full bg-gray-100 text-black hover:bg-gray-200 px-4 py-3 text-sm font-semibold transition-colors duration-300 rounded-lg flex items-center justify-center"
                            >
                              {t('pages.schools.viewDetails', 'View Details')}
                            </Link>
                            <UniversalEnrollButton
                              enrollmentUrl={school.enrollmentForm}
                              variant="primary"
                              size="md"
                              fullWidth={true}
                              className="px-4 py-3 text-sm font-semibold rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Call to Action Section */}

      {/* Features Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
              {t('pages.schools.whyChoose', 'Why Choose ZOE Schools?')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('pages.schools.featuresDescription', 'Our schools offer comprehensive training that combines biblical foundation with practical ministry experience.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">
                {t('pages.schools.feature1Title', 'Biblical Foundation')}
              </h3>
              <p className="text-gray-600">
                {t('pages.schools.feature1Description', 'Deep biblical teaching that establishes strong theological foundations for ministry.')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">
                {t('pages.schools.feature2Title', 'Supernatural Ministry')}
              </h3>
              <p className="text-gray-600">
                {t('pages.schools.feature2Description', 'Practical training in healing, prophecy, deliverance, and supernatural manifestations.')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-black mb-3">
                {t('pages.schools.feature3Title', 'Community & Mentorship')}
              </h3>
              <p className="text-gray-600">
                {t('pages.schools.feature3Description', 'Personal mentorship and lifelong relationships with fellow kingdom ambassadors.')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Schools;